import {
  afterNextRender,
  Component,
  computed,
  effect,
  ElementRef,
  forwardRef,
  HostBinding,
  input,
  InputSignal,
  linkedSignal,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AbstractControl } from '../abstract-control/abstract-control';
import { Icon } from '../../icon/icon';
import { debouncerFn, formatDate, toDateNullable, toInputDate } from '@helpers';
import { DEBOUNCING_MILISECONDS } from '@constants';
import { CSSValuePipe } from '@pipes/css-value-pipe';

@Component({
  selector: 'app-date-picker',
  standalone: true,
  imports: [MatTooltipModule, CSSValuePipe, Icon],
  templateUrl: './date-picker.html',
  styleUrl: './date-picker.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePicker),
      multi: true,
    },
  ],
})
export class DatePicker extends AbstractControl<string | Date | null | undefined, Date | null> {
  // View refs
  $inputElement = viewChild.required<ElementRef<HTMLInputElement>>('input');

  // Bindings
  @HostBinding('style.width')
  get hostWidth(): string { return this.$hostWidth(); }

  // Inputs
  override $value = input<string | Date | null | undefined>(undefined, { alias: 'value' });
  $_value = linkedSignal(this.$value);
  private $debouncedValue = signal<string | Date | null>(null);
  $type = input<'date' | 'datetime-local'>('date', { alias: 'type' });
  $seconds = input<boolean>(false, { alias: 'seconds' });
  $min = input<string, string | Date | null>('', { alias: 'min', transform: v => toInputDate(v, this.$type(), this.$seconds()) });
  $max = input<string, string | Date | null>('', { alias: 'max', transform: v => toInputDate(v, this.$type(), this.$seconds()) });
  $hostWidth = input<string>('fit-content', { alias: 'hostWidth' });
  $_inputWidth = input<string | undefined>(undefined, { alias: 'inputWidth' });
  $_placeholder = input<string | null>(null, { alias: 'placeholder' });
  override $inputFontSize: InputSignal<string> = input('1.55rem', { alias: 'inputFontSize' });

  // Outputs
  override valueChange = output<Date | null>();

  // Signals / atributos
  firstRenderDone: boolean = false;
  override $controlValue = signal<Date | null>(null);
  private readonly debouncedUpdateAndEmit = debouncerFn<Date | null>(DEBOUNCING_MILISECONDS, newDate => this.updateAndEmit(newDate));

  // Valor en string para el atributo del input
  $stringValue = computed<string>(() => {
    const inputValue = this.$_value();
    return toInputDate(inputValue !== undefined ? inputValue : this.$controlValue(), this.$type(), this.$seconds());
  });

  // Valor a mostrar en el párrafo
  $displayValue = computed<string>(() => {
    return formatDate(toDateNullable(this.$debouncedValue(), this.$type()), 'dd-MM-yyyy');
  });

  // Ancho del input
  $inputWidth = computed<string>(() => {
    const inputWidth = this.$_inputWidth();
    if (inputWidth !== undefined)
      return inputWidth;

    const hostWidth = this.$hostWidth();
    if (hostWidth !== 'fit-content' || this.$type() !== 'date')
      return '';

    if (this.$placeholder() === null && this.$displayValue().trim() === '')
      return 'auto';

    return '';
  });

  // Valor a mostrar como placeholder
  $placeholder = computed<string | null>(() => {
    const placeholder = this.$_placeholder();
    if (placeholder === null)
      return null;

    const displayValue = this.$displayValue();
    if (displayValue.trim() !== '')
      return null;

    return placeholder;
  });

  // Valor en tipo Date de la fecha seleccionable mínima
  private $minDateValue = computed<Date | null>(() => toDateNullable(this.$min(), this.$type()));

  // Valor en Date de la fecha seleccionable máxima
  private $maxDateValue = computed<Date | null>(() => toDateNullable(this.$max(), this.$type()));

  constructor () {
    super();

    // Debouncer para el signal de valor
    effect(onCleanup => {
      const value = this.$_value() !== undefined ? this.$_value() as string | Date | null : this.$controlValue();
      let timeout: number | null = null;
      if (!this.firstRenderDone)
        this.$debouncedValue.set(value);
      else
        timeout = setTimeout(() => {
          this.$debouncedValue.set(value);
        }, 25);
      onCleanup(() => {
        if (timeout !== null)
          clearTimeout(timeout);
      });
    });

    // Verificar si se recibió un valor desde el selector para establecerlo como inicial y / o si se emitirá el valor al iniciar
    afterNextRender(() => {
      if (!this.firstRenderDone) {
        if (this.$emitAtStart()) {
          const inputDate = this.$value();
          this.valueChange.emit(toDateNullable(inputDate !== undefined ? inputDate : this.$controlValue(), this.$type()));
        }
        this.firstRenderDone = true;
      }
    });
  }

  // Maneja el evento de cambio de valor desde el input
  onValueChange(event: Event) {
    const control: HTMLInputElement = event.target as HTMLInputElement;
    let date = toDateNullable(control.value, this.$type());

    // Validar que la hora seleccionada no sea menor a la hora mínima o mayor a la máxima
    if (this.$type() === 'datetime-local' && date !== null) {
      const minDate = this.$minDateValue();
      const maxDate = this.$maxDateValue();
      if (minDate !== null && date < minDate) {
        this.$inputElement().nativeElement.value = formatDate(minDate, 'yyyy-MM-ddTHH:mm:ss');
        date = minDate;
      }
      else if (maxDate !== null && date > maxDate) {
        this.$inputElement().nativeElement.value = formatDate(maxDate, 'yyyy-MM-ddTHH:mm:ss');
        date = maxDate;
      }
    }
    if (date === null || this.$type() === 'date')
      this.updateAndEmit(date);
    else
      this.debouncedUpdateAndEmit(date);
  }

  // Actualiza y emite el nuevo valor indicado
  private updateAndEmit(newValue: Date | null) {
    if (this.$value() !== undefined)
      this.$_value.set(newValue);
    this.updateValue(newValue);
    this.valueChange.emit(newValue);
  }
}
